import { useState } from "react";
import { CheckIcon } from "@icons";
import { Button } from "@design-system/Button/Button";
import { HStack } from "@design-system/HStack/HStack";
import { Tag } from "@design-system/Tag/Tag";
import { VStack } from "@design-system/VStack/VStack";
import styles from "./TagPlayground.module.scss";

const mdDemoLabels = ["Invoices", "Receipts", "Statements"] as const;

const filterDemoLabels = ["from Jan 1, 2026", "Reviewed: Category"] as const;

const lgDemoLabel = "recipient@kick.co";

const numericSizes = ["xs", "sm", "md"] as const;

const numericValues = ["1", "12", "128"] as const;

const NumericTag = ({
    size,
    value,
}: {
    size: "xs" | "sm" | "md";
    value: string;
}) => {
    switch (size) {
        case "xs":
            return (
                <Tag size="xs" numeric>
                    {value}
                </Tag>
            );
        case "sm":
            return (
                <Tag size="sm" numeric>
                    {value}
                </Tag>
            );
        case "md":
            return (
                <Tag size="md" numeric>
                    {value}
                </Tag>
            );
    }
};

const MdRemovableTag = ({
    label,
    onRemove,
}: {
    label: string;
    onRemove: (label: string) => void;
}) => (
    <Tag
        size="md"
        leftIcon={<CheckIcon />}
        onRemove={() => {
            onRemove(label);
        }}
        removeLabel={`Remove ${label}`}
    >
        {label}
    </Tag>
);

/**
 * Stateful removable tags: md left-icon chips, md filter pills whose label is
 * a button beside the X, and one lg text tag. Reset restores the original set.
 */
export const TagRemovableDemo = () => {
    const [mdLabels, setMdLabels] = useState<readonly string[]>(mdDemoLabels);
    const [filterLabels, setFilterLabels] =
        useState<readonly string[]>(filterDemoLabels);
    const [openedFilter, setOpenedFilter] = useState<string>();
    const [lgLabel, setLgLabel] = useState<string | undefined>(lgDemoLabel);

    const handleReset = () => {
        setMdLabels(mdDemoLabels);
        setFilterLabels(filterDemoLabels);
        setOpenedFilter(undefined);
        setLgLabel(lgDemoLabel);
    };

    const handleRemoveMd = (label: string) => {
        setMdLabels(mdLabels.filter((item) => item !== label));
    };

    const handleRemoveFilter = (label: string) => {
        setFilterLabels(filterLabels.filter((item) => item !== label));
    };

    const handleRemoveLg = () => {
        setLgLabel(undefined);
    };

    return (
        <VStack className={styles.section} gap={16} padding={24}>
            <VStack gap={4}>
                <h2 className={styles.sectionTitle}>Removable demo</h2>
                <span className={styles.sectionDescription}>
                    md and lg text and icon tags take an X. With onClick too,
                    the label is its own button beside the X, as in applied
                    filter pills (lg accent, to match the 28px filter bar).
                </span>
            </VStack>
            <HStack gap={8} align="center" wrap>
                {filterLabels.map((label) => (
                    <Tag
                        key={label}
                        size="lg"
                        color="accent"
                        onClick={() => {
                            setOpenedFilter(label);
                        }}
                        onRemove={() => {
                            handleRemoveFilter(label);
                        }}
                        removeLabel={`Remove ${label}`}
                    >
                        {label}
                    </Tag>
                ))}
                <span className={styles.sectionDescription}>
                    {openedFilter === undefined
                        ? "Click a label to open its filter"
                        : `Opened ${openedFilter}`}
                </span>
            </HStack>
            <HStack gap={12} align="center" wrap>
                <HStack wrap align="center" gap={8}>
                    {mdLabels.map((label) => (
                        <MdRemovableTag
                            key={label}
                            label={label}
                            onRemove={handleRemoveMd}
                        />
                    ))}
                    {lgLabel !== undefined && (
                        <Tag
                            size="lg"
                            onRemove={handleRemoveLg}
                            removeLabel={`Remove ${lgLabel}`}
                        >
                            {lgLabel}
                        </Tag>
                    )}
                </HStack>
                <Button variant="secondary" size="sm" onClick={handleReset}>
                    Reset
                </Button>
            </HStack>
        </VStack>
    );
};

/**
 * One-, two-, and three-digit numeric tags at xs/sm/md — square at one digit,
 * then growing with the value.
 */
export const TagNumericStrip = () => (
    <VStack className={styles.section} gap={16} padding={24}>
        <VStack gap={4}>
            <h2 className={styles.sectionTitle}>Numeric strip</h2>
            <span className={styles.sectionDescription}>
                Square at one digit; width grows with two and three digits.
            </span>
        </VStack>
        <VStack gap={12}>
            {numericSizes.map((size) => (
                <HStack key={size} gap={16} align="center">
                    <span className={styles.rowLabel}>{size}</span>
                    <HStack wrap align="center" gap={8}>
                        {numericValues.map((value) => (
                            <NumericTag key={value} size={size} value={value} />
                        ))}
                    </HStack>
                </HStack>
            ))}
        </VStack>
    </VStack>
);
